import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { BarChartIcon, BankNoteIcon, CalendarIcon, HomeIcon, SettingsIcon, LargeSearchIcon } from '../../icons';
import { PATHS } from '../../paths';
import { type LoggedInUser, useAuthState } from '../../providers/AuthProvider';
import { usePathWithDateRange } from '../../providers/DateRangeProvider';
import { cx } from '../../utils/cx';
import Button from '../base/buttons/Button';
import Tooltip from '../base/Tooltip';
import MantleHorizontalLockupPrimary from '../logos/MantleHorizontalLockupPrimary';
import EntitySearchModal from '../search/EntitySearchModal';

import NavAccountCard from './NavAccountCard';
import NavItem from './NavItem';
import NavList from './NavList';

import type { NavItemType } from './config';

interface IBaseSidebarProps {
  user: LoggedInUser;
  /** URL of the currently active item. */
  activeUrl?: string;
  /** List of items to display. */
  items: NavItemType[];
  /** List of footer items to display. */
  footerItems?: NavItemType[];
  /** Whether to show the account card. */
  showAccountCard?: boolean;
  /** Additional CSS classes to apply to the sidebar. */
  className?: string;
}

export const BaseSidebar = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  className,
}: IBaseSidebarProps) => {
  const MAIN_SIDEBAR_WIDTH = 296;
  const [isEntitySearchModalOpen, setIsEntitySearchModalOpen] = React.useState(false);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsEntitySearchModalOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const content = (
    <aside
      style={
        {
          '--width': `${MAIN_SIDEBAR_WIDTH}px`,
        } as React.CSSProperties
      }
      className={cx(
        'lg:w-(--width) flex h-full w-full max-w-full flex-col justify-between overflow-auto pt-4 lg:pt-6',
        className
      )}
    >
      <div className="flex w-full justify-between px-4 lg:px-5">
        <div className="flex h-8 items-center">
          <MantleHorizontalLockupPrimary className="h-8 w-auto" role="img" aria-label="Mantle" />
        </div>
        <Tooltip title="Search">
          <Button
            aria-label="Search"
            color="tertiary"
            iconLeading={LargeSearchIcon}
            onClick={() => setIsEntitySearchModalOpen(true)}
          />
        </Tooltip>
      </div>

      <NavList activeUrl={activeUrl} items={items} />

      <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
        {footerItems.length > 0 && (
          <ul className="flex flex-col">
            {footerItems.map((item) => (
              <li key={item.label} className="py-0.5">
                <NavItem
                  icon={item.icon}
                  href={item.href}
                  type="link"
                  current={(item.href?.split('?')[0] ?? '') === activeUrl}
                >
                  {item.label}
                </NavItem>
              </li>
            ))}
          </ul>
        )}

        {showAccountCard && <NavAccountCard user={user} />}
      </div>

      <EntitySearchModal isOpen={isEntitySearchModalOpen} onOpenChange={setIsEntitySearchModalOpen} />
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar navigation */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex">{content}</div>

      {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
      <div
        style={{
          paddingLeft: MAIN_SIDEBAR_WIDTH,
        }}
        className="invisible hidden shrink-0 lg:sticky lg:bottom-0 lg:left-0 lg:top-0 lg:block"
      />
    </>
  );
};

const MainSidebar = () => {
  const { user, loading } = useAuthState();
  const location = useLocation();
  const addDateParamsToPath = usePathWithDateRange();
  const activeUrl = location.pathname;

  if (loading) {
    return null;
  }

  if (!user) {
    return null;
  }

  const rawItems: NavItemType[] = [
    {
      label: 'Dashboard',
      href: PATHS.DASHBOARD,
      icon: HomeIcon,
    },
    {
      label: 'Accounts',
      href: PATHS.ACCOUNTS,
      icon: BarChartIcon,
    },
    {
      label: 'Spending',
      href: PATHS.SPENDING(),
      icon: BankNoteIcon,
    },
    {
      label: 'Recurring',
      href: PATHS.RECURRING_TRANSACTIONS,
      icon: CalendarIcon,
    },
    ...(user.isAdmin
      ? [
          {
            label: 'Admin',
            icon: SettingsIcon,
            items: [
              {
                label: 'Alpha allow list',
                href: PATHS.ADMIN_ALPHA_ALLOW_LIST,
              },
              {
                label: 'Anomaly feedback',
                href: PATHS.ADMIN_USERS_WITH_TRANSACTION_REVIEWS,
              },
              {
                label: 'Bank connections',
                href: PATHS.ADMIN_BANK_CONNECTIONS,
              },
              {
                label: 'Bill managment',
                href: PATHS.ADMIN_BILL_MANAGMENT,
              },
              {
                label: 'Impersonate user',
                href: PATHS.ADMIN_IMPERSONATE_USER,
              },
              {
                label: 'Financial chat',
                href: PATHS.ADMIN_CHAT,
              },
              {
                label: 'Processor statuses',
                href: PATHS.PROCESSOR_STATUSES,
              },
              {
                label: 'Obligations',
                href: PATHS.OBLIGATIONS,
              },
              {
                label: 'Sentry test',
                href: PATHS.ADMIN_SENTRY_TEST,
              },
              {
                label: 'Environment',
                href: PATHS.ADMIN_ENVIRONMENT,
              },
            ],
          },
        ]
      : []),
  ];

  const items: NavItemType[] = rawItems.map((item) => {
    if (item.items?.length) {
      return {
        ...item,
        href: item.href ? addDateParamsToPath(item.href) : undefined,
        items: item.items.map((subItem) => ({
          ...subItem,
          href: addDateParamsToPath(subItem.href),
        })),
      };
    }
    return {
      ...item,
      href: item.href ? addDateParamsToPath(item.href) : undefined,
    };
  });

  return <BaseSidebar items={items} user={user} activeUrl={activeUrl} />;
};

export default MainSidebar;
